<template>
  <div class="product-categories-show-view">
    <h1 v-if="category">{{category.name}}</h1>
    <h1 v-else-if="tenant">{{tenant.name}}</h1>

    <link-button
      :to="{ name: 'new-product-category', query: { parentId: id, tenantId } }"
      icon="plus"
      type="primary"
    >
      <span v-if="id">{{$t('actions.newChild')}}</span>
      <span v-else>{{$t('actions.newRoot')}}</span>
    </link-button>

    <link-button
      :to="{ name: 'edit-product-category', params: { id } }"
      icon="pencil"
      v-if="category"
    >{{$t('actions.edit')}}</link-button>

    <common-button
      @click="deleteProductCategory"
      icon="trash"
      type="danger"
      v-if="category"
    >{{$t('actions.delete')}}</common-button>

    <search-input
      class="uk-align-right"
      ref="search"
      v-if="showCategoryList"
      v-model="searchQuery"
    />

    <template v-if="showCategoryList">
      <h3 v-if="category">{{$t('headlines.subCategories')}}</h3>

      <data-grid
        :columns="gridColumns"
        :is-loading="isLoading"
        :page-info="productCategories.pagination"
        :rows="productCategories.entries"
        :sort-info="productCategories.sort"
        :sortable="['name', 'categoryNumber', 'updatedAt']"
        @sort="sortBy"
        class="uk-margin-medium"
        v-if="gridLoaded"
      >
        <span slot="empty">{{$t('hints.empty')}}</span>
        <template scope="props" slot="name">
          <router-link
            :to="{ name: 'product-category', params: { id: props.row.id } }"
          >{{props.value}}</router-link>
        </template>
        <template scope="props" slot="categoryNumber">
          <span v-if="props.value">{{props.value}}</span>
          <text-placeholder v-else />
        </template>
        <template scope="props" slot="updatedAt">
          <timeago :auto-update="60" :since="props.value" />
        </template>
        <template scope="props" slot="actions">
          <data-grid-actions
            :edit-route="{
              name: 'edit-product-category',
              params: { id: props.row.id }
            }"
            @remove="deleteRecordWhenConfirmed(props.row)"
          />
        </template>
      </data-grid>

      <div
        class="loader-container uk-container uk-margin-medium-top uk-text-center"
        v-else
      >
        <loader />
      </div>
    </template>

    <template v-if="category">
      <h3>{{$t('headlines.attributes')}}</h3>

      <attribute-table
        :data="category"
        :fields="[
          'name',
          'ancestors',
          'categoryNumber',
          'path',
          'aliases',
          'insertedAt',
          'updatedAt'
        ]"
      >
        <template scope="aliases" slot="aliases">
          <span v-if="aliases.value.length">{{ aliases.value.join(', ') }}</span>
          <text-placeholder v-else />
        </template>
        <template scope="ancestors" slot="ancestors">
          <product-category-path
            :categories="ancestors.value"
            v-if="ancestors.value.length"
          >
            <template scope="props" slot="item">
              <router-link
                :to="{
                  name: 'product-category',
                  params: { id: props.category.id }
                }"
              >{{ props.category.name }}</router-link>
            </template>
          </product-category-path>
          <text-placeholder v-else />
        </template>
        <template scope="insertedAt" slot="insertedAt">
          <timeago :auto-update="60" :since="insertedAt.value" />
        </template>
        <template scope="updatedAt" slot="updatedAt">
          <timeago :auto-update="60" :since="updatedAt.value" />
        </template>
      </attribute-table>

      <h3>{{$t('headlines.productFamilies')}}</h3>

      <link-button
        :to="{ name: 'product-families', query: { categoryId: id } }"
        v-if="category"
      >{{$t('actions.showProductFamilies')}}</link-button>
    </template>
  </div>
</template>

<script>
import UIkit from 'uikit';
import AttributeTable from '@/components/AttributeTable';
import CommonButton from '@/components/CommonButton';
import DataGridActions from '@/components/DataGridActions';
import deleteProductCategoryMutation
  from '@/api/mutations/delete-product-category.graphql';
import IndexView from '@/mixins/IndexView';
import productCategoriesQuery from '@/api/queries/product-categories.graphql';
import ProductCategoryPath from '@/components/ProductCategoryPath';
import productCategoryQuery from '@/api/queries/product-category.graphql';
import tenantQuery from '@/api/queries/tenant.graphql';
import TextPlaceholder from '@/components/TextPlaceholder';

export default {
  name: 'product-categories-show-view',
  mixins: [
    IndexView('productCategories', {
      gridFields: ['name', 'categoryNumber', 'updatedAt'],
      listQuery: productCategoriesQuery,
      deleteMutation: deleteProductCategoryMutation,
      defaultFilter() {
        const filter = { parentId: this.id || null };
        if (this.tenantId) {
          filter.tenantId = this.tenantId;
        }
        return filter;
      },
    }),
  ],
  components: {
    AttributeTable,
    CommonButton,
    DataGridActions,
    ProductCategoryPath,
    TextPlaceholder,
  },
  computed: {
    isSuccessfullyLoaded() {
      return this.isLoaded &&
        (!this.parentId || (this.parentId && this.parent));
    },
    id() {
      return this.$route.params.id;
    },
    tenantId() {
      return this.$route.params.tenantId;
    },
    breadcrumbs() {
      const items = [];
      if (this.tenant) {
        items.push({
          key: 'tenant',
          text: this.tenant.name,
          route: {
            name: 'product-categories-for-tenant',
            params: { tenantId: this.tenant.id },
          },
        });
      }
      if (this.category) {
        this.category.ancestors.forEach((ancestor) => {
          items.push({
            key: `show-${ancestor.id}`,
            text: ancestor.name,
            route: {
              name: 'product-category',
              params: { id: ancestor.id },
            },
          });
        });
        items.push({
          key: `show-${this.category.id}`,
          text: this.category.name,
          route: {
            name: 'product-category',
            params: { id: this.category.id },
          },
        });
      }
      return items;
    },
    category() {
      if (!this.id) return null;
      return this.parentCategory;
    },
    tenant() {
      if (this.tenantId) return this.rootTenant;
      if (this.category) return this.category.tenant;
      return null;
    },
    showCategoryList() {
      return !this.category ||
        (this.productCategories && this.productCategories.entries.length > 0);
    },
  },
  apollo: {
    parentCategory: {
      query: productCategoryQuery,
      variables() {
        return { id: this.id };
      },
      loadingKey: 'loading',
      fetchPolicy: 'network-only',
      update({ productCategory }) {
        return productCategory;
      },
      skip() {
        return !this.isAuthenticated || !this.id;
      },
    },
    rootTenant: {
      query: tenantQuery,
      variables() {
        return { id: this.tenantId };
      },
      loadingKey: 'loading',
      fetchPolicy: 'network-only',
      update({ tenant }) {
        return tenant;
      },
      skip() {
        return !this.isAuthenticated || !this.tenantId;
      },
    },
  },
  methods: {
    async deleteProductCategory() {
      const name = this.category.name;
      try {
        await UIkit.modal.confirm(this.$t('confirmations.delete', { name }));
        await this.deleteRecord(this.category, false);
        if (this.category.parent) {
          this.$router.push({
            name: 'product-category',
            params: { id: this.category.parent.id },
          });
        } else {
          this.$router.push({
            name: 'product-categories-for-tenant',
            params: { tenantId: this.category.tenant.id },
          });
        }
      } catch (_) {
        // Do nothing
      }
    },
  },
  i18n: {
    messages: {
      de: {
        actions: {
          newRoot: 'Produktkategorie erstellen',
          newChild: 'Unterkategorie erstellen',
          showProductFamilies: 'Verknüpfte Produktfamilien anzeigen',
        },
        confirmations: {
          delete: 'Produktkategorie <strong>{name}</strong> wirklich löschen?',
        },
        headlines: {
          attributes: 'Eigenschaften',
          productFamilies: 'Produktfamilien',
          subCategories: 'Unterkategorien',
        },
        hints: {
          empty: 'Keine Produktkategorien gefunden.',
        },
        titles: {
          productCategories: 'Produktkategorien',
        },
        toasts: {
          deleted: 'Produktkategorie wurde gelöscht: {name}',
        },
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// ShowView.vue?a676ddf8